import React from 'react';
import type * as Immutable from 'immutable';
import { Popover } from '@milanote/roma';
import { Button, Dialog } from 'react-aria-components';

import type { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import ElementIcon from '../../../../../../components/elementIcon/ElementIcon';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { useWorkspacePopoverBoundary } from '../../../../../workspaceBoundary/WorkspaceBoundaryContext';
import useToolbarPopover from '../../../../hooks/useToolbarPopover';
import Tool from '../../../Tool';
import { getSelectedIcon, getSelectedImage } from '../iconToolUtils';
import IconPicker from './IconPicker';

// Shared with the legacy tool until the toolbar tools are rebuilt.
import '../IconTool.scss';

type IconToolV2Props = {
    name?: string;
    selectedElements: Immutable.List<ImMNElement>;
};

/**
 * Board icon toolbar tool that opens the Roma icon picker.
 * The trigger keeps the legacy look: the first selected board's icon, or its custom image.
 */
const IconToolV2: React.FC<IconToolV2Props> = ({ name, selectedElements }) => {
    const translate = useTranslator('popup', 'icon');
    const { isOpen, onOpenChange } = useToolbarPopover();
    const workspaceBoundary = useWorkspacePopoverBoundary();
    const selectedImage = getSelectedImage(selectedElements);
    const selectedIcon = getSelectedIcon(selectedElements);

    const triggerContent = selectedImage ? (
        <img src={selectedImage} alt="" className="absolute inset-0 size-full object-cover" />
    ) : (
        <ElementIcon icon={selectedIcon} />
    );

    return (
        <div className="ToolbarPopupTool">
            <Popover.DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
                <Button className="Button" aria-label={name}>
                    {/* `Tool`, not `ToolbarTool`: its `.icon .Icon` sizing would stretch the icon to fill the trigger */}
                    <Tool className="IconTool" name={name} isActive={isOpen}>
                        {triggerContent}
                    </Tool>
                </Button>
                <Popover placement="end" offset={4} {...workspaceBoundary}>
                    <Dialog aria-label={translate('title')} className="flex min-h-0 flex-col outline-hidden">
                        <IconPicker selectedElements={selectedElements} />
                    </Dialog>
                </Popover>
            </Popover.DialogTrigger>
        </div>
    );
};

export default IconToolV2;
